/**
 * Tabs de contestada do laudo + derivação de rótulos/estado (E36.F5). // ux-neutrality:allow doc-comment
 *
 * Extraído de `LaudoCasoPage` para manter o arquivo da página sob o limite de // ux-neutrality:allow doc-comment
 * linhas (mesmo padrão de `WorkspaceContestedTabs`). Apenas apresentação: uma // ux-neutrality:allow doc-comment
 * tab por contestada `completed`, badge de estado (rascunho/editando/aprovada). // ux-neutrality:allow doc-comment
 */
import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { SignatureRecordData } from '@/lib/case_signatures';
import type { CaseAnalysisData } from '@/lib/case_workspace';
import type { NarrativeRecordData } from '@/lib/laudo';

export interface ContestadaTab {
  caseAnalysisId: string;
  letra: string; // 'a', 'b', 'c', ...
  label: string;
}

export function deriveContestadaTabs(
  analyses: CaseAnalysisData[],
  signatures: SignatureRecordData[],
): ContestadaTab[] {
  const labelById = new Map<string, string>();
  for (const s of signatures) {
    if (s.kind === 'contested') labelById.set(s.id, s.label ?? '');
  }
  const completed = analyses.filter((a) => a.status === 'completed');
  return completed.map((a, i) => ({
    caseAnalysisId: a.id,
    letra: String.fromCharCode(97 + i),
    label:
      labelById.get(a.contested_signature_id) ?? `${ptBR.laudoCaso.contestadaItemLabel} ${i + 1}`,
  }));
}

type TabStatus = 'rascunho' | 'editando' | 'aprovada';

function tabStatus(n: NarrativeRecordData | null | undefined): TabStatus {
  if (n === null || n === undefined) return 'rascunho';
  return n.approved_at !== null ? 'aprovada' : 'editando';
}

function tabStatusLabel(status: TabStatus): string {
  switch (status) {
    case 'rascunho':
      return ptBR.laudoCaso.tabs.statusRascunho;
    case 'editando':
      return ptBR.laudoCaso.tabs.statusEditando;
    case 'aprovada':
      return ptBR.laudoCaso.tabs.statusAprovada;
  }
}

interface ContestadaTabsProps {
  tabs: ContestadaTab[];
  activeAnalysisId: string | null;
  narrativesByAnalysisId: Record<string, NarrativeRecordData | null>;
  onSelect: (id: string) => void;
}

export function ContestadaTabs({
  tabs,
  activeAnalysisId,
  narrativesByAnalysisId,
  onSelect,
}: ContestadaTabsProps): JSX.Element {
  return (
    <div
      className="laudo-page__tabs"
      role="tablist"
      aria-label={ptBR.laudoCaso.tabs.ariaLabel}
      data-testid="laudo-contestada-tabs"
    >
      {tabs.map((tab) => {
        const status = tabStatus(narrativesByAnalysisId[tab.caseAnalysisId]);
        const isActive = tab.caseAnalysisId === activeAnalysisId;
        return (
          <button
            key={tab.caseAnalysisId}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`laudo-tab-panel-${tab.caseAnalysisId}`}
            id={`laudo-tab-${tab.caseAnalysisId}`}
            className={isActive ? 'laudo-page__tab is-active' : 'laudo-page__tab'}
            onClick={() => onSelect(tab.caseAnalysisId)}
            data-testid={`laudo-tab-${tab.letra}`}
            data-status={status}
          >
            <span className="laudo-page__tab-letra">{tab.letra})</span>{' '}
            <span className="laudo-page__tab-label">{tab.label}</span>
            <Badge
              variant={status === 'aprovada' ? 'success' : 'neutral'}
              data-testid={`laudo-tab-${tab.letra}-badge`}
            >
              {tabStatusLabel(status)}
            </Badge>
          </button>
        );
      })}
    </div>
  );
}
